import React, { useRef, useState } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { Gauge } from "@mui/x-charts/Gauge";
import { RadarChart } from "@mui/x-charts/RadarChart";
import {
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import Tooltip from "@mui/material/Tooltip";
import { toPng, toJpeg, toSvg } from "html-to-image";
import { ChartSpec, ChartType, ChartSeries } from "./chartTypes";

const CHART_HEIGHT = 320;

// Chart types that share a "categories + values" data shape and can therefore
// be swapped between each other client-side without re-querying.
const SWAPPABLE_TYPES: { value: ChartType; label: string }[] = [
  { value: "bar", label: "Bar" },
  { value: "stackedBar", label: "Stacked bar" },
  { value: "line", label: "Line" },
  { value: "area", label: "Area" },
  { value: "pie", label: "Pie" },
];
const SWAPPABLE = new Set<ChartType>(SWAPPABLE_TYPES.map((t) => t.value));

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-z0-9_-]+/gi, "_").slice(0, 80) || "chart";
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.download = filename;
  a.href = dataUrl;
  a.click();
}

// Extract the underlying categories + series from a categorical chart, whether
// it was authored as a bar/line/area/stackedBar (xAxis + series) or as a pie.
function getCategoricalData(
  chart: ChartSpec,
): { categories: string[]; series: ChartSeries[] } | null {
  if (chart.xAxis && chart.series && chart.series.length) {
    return { categories: chart.xAxis, series: chart.series };
  }
  if (chart.pie && chart.pie.length) {
    return {
      categories: chart.pie.map((d) => d.label),
      series: [{ label: chart.title ?? "Value", data: chart.pie.map((d) => d.value) }],
    };
  }
  return null;
}

function renderCategorical(
  type: ChartType,
  categories: string[],
  series: ChartSeries[],
): React.JSX.Element {
  if (type === "pie") {
    // Pie shows a single series; use the first one.
    const first = series[0]?.data ?? [];
    const data = categories.map((label, i) => ({
      id: i,
      label,
      value: first[i] ?? 0,
    }));
    return (
      <PieChart
        height={CHART_HEIGHT}
        series={[
          {
            data,
            innerRadius: 35,
            outerRadius: 110,
            paddingAngle: 2,
            cornerRadius: 4,
          },
        ]}
      />
    );
  }

  if (type === "line" || type === "area") {
    const s = series.map((ser) => (type === "area" ? { ...ser, area: true } : ser));
    return (
      <LineChart
        height={CHART_HEIGHT}
        xAxis={[{ data: categories, scaleType: "point" }]}
        series={s}
        margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
      />
    );
  }

  // bar / stackedBar
  const s =
    type === "stackedBar"
      ? series.map((ser) => ({ ...ser, stack: ser.stack ?? "total" }))
      : series;
  return (
    <BarChart
      height={CHART_HEIGHT}
      xAxis={[{ data: categories, scaleType: "band" }]}
      series={s}
      margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
    />
  );
}

export function ChartRenderer({
  chart,
  captureId,
}: {
  chart: ChartSpec;
  captureId?: string;
}) {
  const title = chart.title;
  const exportRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentType, setCurrentType] = useState<ChartType>(chart.type);
  const [copied, setCopied] = useState(false);

  const handleExport = async (format: "png" | "jpeg" | "svg") => {
    setAnchorEl(null);
    if (!exportRef.current) return;
    const fname = sanitizeFilename(title ?? "chart");
    const opts = { pixelRatio: 2, backgroundColor: "#ffffff" };
    const url =
      format === "png"
        ? await toPng(exportRef.current, opts)
        : format === "jpeg"
          ? await toJpeg(exportRef.current, { ...opts, quality: 0.95 })
          : await toSvg(exportRef.current);
    triggerDownload(url, `${fname}.${format}`);
  };

  // Copy the chart as a PNG image to the clipboard, so it can be pasted into
  // Word / PowerPoint / Outlook etc.
  const handleCopy = async () => {
    if (!exportRef.current) return;
    try {
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error(e);
      alert(
        "Could not copy the chart. Your browser may not support copying images; use the download options instead.",
      );
    }
  };

  const categorical = getCategoricalData(chart);
  // The switcher is offered only when the chart is part of the categorical
  // family (the common LLM-generated case). Special types render as authored.
  const canSwitch = categorical !== null && SWAPPABLE.has(chart.type);

  let body: React.JSX.Element | null = null;

  if (canSwitch && categorical) {
    body = renderCategorical(currentType, categorical.categories, categorical.series);
  } else {
    switch (chart.type) {
      case "bar":
      case "stackedBar":
      case "line":
      case "area":
      case "pie": {
        if (categorical) {
          body = renderCategorical(chart.type, categorical.categories, categorical.series);
        }
        break;
      }
      case "scatter": {
        const series = (chart.scatter ?? []).map((sr) => ({
          label: sr.label,
          data: sr.data.map((p, i) => ({ id: i, x: p.x, y: p.y })),
        }));
        body = (
          <ScatterChart
            height={CHART_HEIGHT}
            series={series}
            margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
          />
        );
        break;
      }
      case "sparkline": {
        const data = chart.sparkline?.data ?? [];
        body = <SparkLineChart data={data} height={120} showTooltip showHighlight />;
        break;
      }
      case "gauge": {
        const g = chart.gauge;
        if (g) {
          body = (
            <Gauge
              width={220}
              height={220}
              value={g.value}
              valueMin={g.min}
              valueMax={g.max}
              startAngle={-110}
              endAngle={110}
              text={({ value, valueMax }) => `${value} / ${valueMax}`}
            />
          );
        }
        break;
      }
      case "radar": {
        const r = chart.radar;
        if (r) {
          body = (
            <RadarChart
              height={CHART_HEIGHT}
              series={r.series.map((s) => ({ label: s.label, data: s.data }))}
              radar={{ metrics: r.metrics, max: r.max }}
            />
          );
        }
        break;
      }
      default:
        body = null;
    }
  }

  if (!body) return null;

  return (
    <div
      style={{
        marginTop: 12,
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          padding: "4px 8px 0",
        }}
      >
        {canSwitch && (
          <FormControl size="small" variant="standard" sx={{ minWidth: 110 }}>
            <Select
              value={currentType}
              onChange={(e) => setCurrentType(e.target.value as ChartType)}
              aria-label="Chart type"
              disableUnderline
              sx={{ fontSize: 13 }}
            >
              {SWAPPABLE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Tooltip title={copied ? "Copied" : "Copy chart"}>
          <IconButton size="small" aria-label="Copy chart" onClick={handleCopy}>
            {copied ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          aria-label="Export chart"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleExport("png")}>PNG</MenuItem>
          <MenuItem onClick={() => handleExport("jpeg")}>JPEG</MenuItem>
          <MenuItem onClick={() => handleExport("svg")}>SVG</MenuItem>
        </Menu>
      </div>
      <div id={captureId} ref={exportRef} style={{ padding: 12 }}>
        {title && (
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
        )}
        {body}
      </div>
    </div>
  );
}
